// ----------------------------------------------------------------------------
// Product Form Extension: Gift Recipient Validation
// ----------------------------------------------------------------------------
// Gift products (the `product.gifting` template) render a Recharge "gifting
// widget" app block that injects the recipient fields (name, email, message,
// send date) into the product form client-side, after the theme JS runs.
//
// The product form submits over AJAX with native validation disabled
// (JustValidate sets `novalidate`), and there is no submit-time rule for the
// injected recipient email. As a result a gift could be added to the cart — and
// purchased — with no recipient to redeem it (see the "gift order with no
// giftee" bug).
//
// This extension blocks the gift from being added to the cart until a valid
// recipient email is entered. It listens for `submit` in the capture phase on
// the document, which runs *before* the theme's own form-level submit handler
// (Shopify's ProductForm, bound in the bubble phase) — so stopping propagation
// there reliably prevents the add-to-cart for both the Add to Cart and Buy Now
// buttons (both are `type="submit"` on the same form). It applies to every
// gifting-template product automatically (they all share this widget).
//
// The listeners are delegated on the document (bound once at module load) so a
// theme-editor section re-render — which swaps in a fresh <form> — reuses them
// rather than accumulating a listener per render.
//
// Why not a JustValidate rule (as the quantity inputs use)? The Add to Cart
// button reacts to JustValidate's form-validity events but does not reliably
// re-enable once this asynchronously-injected field becomes valid, which would
// leave a valid gift un-purchasable. Gating the submit directly avoids coupling
// to that shared button's state entirely.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import translate from 'scripts/helpers/iamota-translate';

// -------------------------------------
// Config
// -------------------------------------

// Only gifting-template products get the `.product--gifting` wrapper (see
// main-product.liquid) — used to scope the guard to gift forms.
var GIFTING_CONTEXT_SELECTOR = '.product--gifting';

// The Recharge gifting widget injects this field. Both the id and the line-item
// property name are stable Recharge hooks (never hashed build-artifact classes);
// on the live widget they are the same element, so either match resolves it.
var RECIPIENT_EMAIL_SELECTOR = '#rc_gift_recipient_email, [name="properties[__rc_gift_recipient_email]"]';

// Pragmatic email shape check — matches the intent of an <input type="email">
// (a single "@" with a dotted domain) without over-constraining valid addresses.
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Tracks the error message this extension is currently showing per form, so we
// only clear the shared error container when it still holds *our* message.
var shownErrorByForm = new WeakMap();

// -------------------------------------
// Helpers
// -------------------------------------

/**
 * The gift product form, if `node` is inside one (and only a gifting product).
 * @param {EventTarget} node
 * @returns {HTMLFormElement|null}
 */
function getGiftingForm(node) {
  if (!node || typeof node.closest !== 'function') return null;
  var form = node.closest('form');
  if (!form || !form.closest(GIFTING_CONTEXT_SELECTOR)) return null;
  return form;
}

/**
 * The `<iam-product-form>` wrapper for a form (exposes setErrorState/errorContainer).
 * @param {HTMLFormElement} form
 * @returns {HTMLElement|null}
 */
function getIamForm(form) {
  var iamForm = form.closest('iam-product-form');
  return iamForm && typeof iamForm.setErrorState === 'function' ? iamForm : null;
}
function isValidEmail(field) {
  return EMAIL_REGEX.test((field.value || '').trim());
}
function showError(form, emailField) {
  var message = (emailField.value || '').trim() ? translate('Please enter a valid recipient email address.', 'gifting.error.email_invalid') : translate("Please enter the recipient's email so we can send them their gift.", 'gifting.error.email_required');
  var iamForm = getIamForm(form);
  if (!iamForm) return;
  iamForm.setErrorState(message);
  shownErrorByForm.set(form, message);
}
function clearError(form) {
  var message = shownErrorByForm.get(form);
  if (!message) return;
  var iamForm = getIamForm(form);
  // Only clear if the container still holds our message — never clobber an
  // unrelated error that replaced it in the meantime.
  if (iamForm && iamForm.errorContainer && iamForm.errorContainer.textContent.trim() === message.trim()) {
    iamForm.classList.remove('--error');
    iamForm.errorContainer.innerHTML = '';
  }
  shownErrorByForm.delete(form);
}

// -------------------------------------
// Functionality
// -------------------------------------

// Block add-to-cart / buy-now on gift forms until a valid recipient email is
// entered. Capture phase runs before the theme's form-level submit handler.
document.addEventListener('submit', event => {
  var form = getGiftingForm(event.target);
  if (!form) return;
  var emailField = form.querySelector(RECIPIENT_EMAIL_SELECTOR);
  // Widget never loaded — fail open rather than hard-block a legitimate
  // purchase on a third-party failure.
  if (!emailField) return;
  if (isValidEmail(emailField)) {
    clearError(form);
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  showError(form, emailField);
  emailField.focus();
}, true);

// Clear the error as soon as the recipient email becomes valid.
document.addEventListener('input', event => {
  var target = event.target;
  if (!target || typeof target.matches !== 'function' || !target.matches(RECIPIENT_EMAIL_SELECTOR)) return;
  var form = getGiftingForm(target);
  if (form && isValidEmail(target)) clearError(form);
}, true);